import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { EM_DASH } from "@/lib/utils";
import type { QcIncident } from "@/pages/manufacturing/qcIncidentsApi";

function formatDate(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function statusTone(incident: QcIncident): BadgeTone {
  if (incident.status === "resolved") return "success";
  if (incident.pottingRework?.regradePottingGrade === "Fail") return "warning";
  if (incident.pottingRework != null) return "info";
  return "neutral";
}

function statusLabel(incident: QcIncident): string {
  if (incident.status === "resolved") return "Resolved";
  if (incident.pottingRework?.regradePottingGrade === "Fail") return "Rework failed";
  if (incident.pottingRework != null) return "Potting rework";
  return "Unresolved";
}

function incidentTypeLabel(incident: QcIncident): string {
  if (incident.type === "device") return "Device";
  if (incident.type === "clamp") return incident.subtype ? `Clamp (${incident.subtype})` : "Clamp";
  if (incident.type === "pcba") return "PCBA";
  return "Unknown";
}

export function QcIncidentSummary({ incident }: { incident: QcIncident }): JSX.Element {
  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900">Incident #{incident.id}</span>
        <Badge tone={statusTone(incident)}>{statusLabel(incident)}</Badge>
      </div>
      <dl className="grid gap-2 sm:grid-cols-2">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Serial</dt>
          <dd className="font-mono text-slate-900">{incident.serial ?? EM_DASH}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Type</dt>
          <dd className="text-slate-900">{incidentTypeLabel(incident)}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Issue date</dt>
          <dd className="text-slate-900">{formatDate(incident.issueDate)}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Issue person</dt>
          <dd className="text-slate-900">{incident.issuePerson ?? EM_DASH}</dd>
        </div>
      </dl>
      <p className="text-slate-700">{incident.issue ?? "No issue text."}</p>
      {incident.issueNotes?.trim() ? (
        <p className="text-slate-600">
          <span className="font-medium text-slate-700">Notes:</span> {incident.issueNotes.trim()}
        </p>
      ) : null}
      {incident.status === "resolved" ? (
        <dl className="grid gap-2 border-t border-slate-200 pt-2 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Resolution</dt>
            <dd className="text-slate-900">{incident.repairScrap ?? EM_DASH}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Root cause</dt>
            <dd className="text-slate-900">{incident.rootCause ?? EM_DASH}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Resolution date</dt>
            <dd className="text-slate-900">{formatDate(incident.resolutionDate)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Resolution person</dt>
            <dd className="text-slate-900">{incident.resolutionPerson ?? EM_DASH}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Resolution notes</dt>
            <dd className="text-slate-900">{incident.resolutionNotes?.trim() || EM_DASH}</dd>
          </div>
        </dl>
      ) : null}
    </div>
  );
}
